import { Button, Checkbox, Drawer, DropdownMenu, IconButton, Input, Prompt, Select, Text, Textarea, toast } from "@medusajs/ui"
import { XMark } from "@medusajs/icons"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { AddressFields, addressProblem } from "@/components/address-fields"
import { useSellingCountries } from "@/lib/markets"
import { countryName } from "@/lib/geo"
import { DataTable, EmptyState, ErrorState, PageHeader, SaveBar, SearchInput, Section, Thumb, usePage } from "@/components/ui"
import { formatDate, formatMoney } from "@/lib/format"
import { draftLabel, useCompleteDraft, useDeleteDraft, useDraft, useDraftList, useSaveDraft, useSendInvoice, useVariantSearch, type Draft, type DraftAddress, type DraftInput } from "@/lib/drafts"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"

const PAGE = 50

export function DraftsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const { data, isLoading, error } = useDraftList({ q, limit: PAGE, offset: pageIndex * PAGE })
  const navigate = useNavigate()
  const can = useCan()
  const create = can("draft_orders:write") ? <Button size="small" onClick={() => navigate("/orders/drafts/new")}>Create order</Button> : null
  if (error) return <ErrorState error={error} />
  return (
    <>
      <PageHeader title="Drafts" actions={create} />
      <Section>
        <div className="px-4 py-3"><SearchInput value={q} onChange={setQ} placeholder="Search by email" /></div>
        <DataTable<Draft>
          rows={data?.drafts}
          count={data?.count ?? 0}
          pageSize={PAGE}
          pageIndex={pageIndex}
          onPageChange={setPage}
          loading={isLoading}
          rowKey={(d) => d.id}
          onRowClick={(d) => navigate(`/orders/drafts/${d.id}`)}
          empty={<EmptyState title="Manually create orders and invoices" body="Use draft orders to take orders by phone or email, then mark them as paid to create the order." action={create} />}
          columns={[
            { key: "no", header: "Draft order", cell: (d) => <span className="font-medium">{draftLabel(d)}</span> },
            { key: "date", header: "Date", cell: (d) => formatDate(d.updated_at) },
            { key: "customer", header: "Customer", cell: (d) => d.customer_name || d.email || <span className="text-ui-fg-muted">No customer</span> },
            { key: "status", header: "Status", cell: () => <span className="rounded-full bg-ui-tag-orange-bg px-2 py-0.5 text-[12px] text-ui-tag-orange-text">Open</span> },
            { key: "total", header: "Total", className: "text-right", cell: (d) => formatMoney(d.total) },
          ]}
          mobile={(d) => (
            <div className="flex items-center justify-between gap-2">
              <div><Text size="small" weight="plus">{draftLabel(d)}</Text><Text size="xsmall" className="text-ui-fg-subtle">{d.customer_name || d.email || "No customer"}</Text></div>
              <Text size="small">{formatMoney(d.total)}</Text>
            </div>
          )}
        />
      </Section>
    </>
  )
}

type Line = { key: string; variant_id?: string; title: string; subtitle?: string | null; thumbnail?: string | null; unit_price: number; quantity: number }
type State = { lines: Line[]; customer_id: string | null; customer_label: string | null; email: string; address: DraftAddress; shipping_option_id: string; note: string }
const EMPTY: State = { lines: [], customer_id: null, customer_label: null, email: "", address: { country_code: "us" }, shipping_option_id: "", note: "" }
let seq = 0
const k = () => `l${++seq}`

function fromDraft(d: Draft): State {
  return {
    lines: d.items.map((i) => ({ key: k(), variant_id: i.variant_id ?? undefined, title: i.title, subtitle: i.subtitle, thumbnail: i.thumbnail, unit_price: i.unit_price, quantity: i.quantity })),
    customer_id: d.customer_id,
    customer_label: d.customer_name || d.email,
    email: d.email ?? "",
    address: { country_code: "us", ...(d.shipping_address ?? {}) },
    shipping_option_id: d.shipping?.shipping_option_id ?? "",
    note: d.note,
  }
}

const clean = (a: DraftAddress) => Object.fromEntries(Object.entries(a).filter(([key, v]) => typeof v === "string" && v.trim() && !["id", "created_at", "updated_at", "deleted_at", "metadata", "customer_id"].includes(key))) as DraftAddress

export function DraftEditorPage() {
  const { id } = useParams()
  const isNew = !id || id === "new"
  const { data, error } = useDraft(isNew ? undefined : id)
  const can = useCan()
  const writable = can("draft_orders:write")
  const navigate = useNavigate()
  const save = useSaveDraft(isNew ? undefined : id)
  const del = useDeleteDraft(id ?? "")
  const complete = useCompleteDraft(id ?? "")
  const [base, setBase] = useState<State>(EMPTY)
  const [s, setS] = useState<State>(EMPTY)
  const [browse, setBrowse] = useState(false)
  const [custom, setCustom] = useState(false)
  const [confirm, setConfirm] = useState<null | "paid" | "later">(null)
  const [notify, setNotify] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [invoicing, setInvoicing] = useState(false)
  const selling = useSellingCountries()
  const country = (s.address.country_code || "us").toLowerCase()
  // Only the rates of the zone that ships to the address's country.
  const rates = useQuery({ queryKey: ["draft-rates", country], queryFn: () => sdk.client.fetch<{ rates: { id: string; name: string; price: number; free_over: number | null; description: string }[] }>(`/admin/terify/draft-orders/shipping-rates?country=${country}`) })

  useEffect(() => {
    if (data?.draft) {
      const st = fromDraft(data.draft)
      setBase(st)
      setS(st)
    }
  }, [data?.draft.updated_at, data?.draft.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // "Create order" from a customer page: /orders/drafts/new?customer=cus_… prefills customer + default address.
  const [search] = useSearchParams()
  const presetCustomer = isNew ? search.get("customer") : null
  useEffect(() => {
    if (!presetCustomer || !/^cus_[A-Za-z0-9]+$/.test(presetCustomer)) return
    let alive = true
    sdk.admin.customer
      .retrieve(presetCustomer, { fields: "id,email,first_name,last_name,*addresses" } as any)
      .then(({ customer }: any) => {
        if (!alive) return
        const a: any = customer.addresses?.find((x: any) => x.is_default_shipping) ?? customer.addresses?.[0]
        setS((x) => ({
          ...x,
          customer_id: customer.id,
          customer_label: [customer.first_name, customer.last_name].filter(Boolean).join(" ") || customer.email,
          email: customer.email,
          ...(a ? { address: clean({ ...a, country_code: a.country_code ?? "us" }) } : {}),
        }))
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [presetCustomer])

  const dirty = JSON.stringify({ ...s, lines: s.lines.map(({ key: _, ...l }) => l) }) !== JSON.stringify({ ...base, lines: base.lines.map(({ key: _, ...l }) => l) })
  const subtotal = s.lines.reduce((a, l) => a + l.unit_price * l.quantity, 0)
  const rate = rates.data?.rates.find((r) => r.id === s.shipping_option_id)
  const shipping = rate ? (rate.free_over != null && subtotal >= rate.free_over ? 0 : rate.price) : 0
  const set = (patch: Partial<State>) => setS((x) => ({ ...x, ...patch }))
  const setLine = (key: string, patch: Partial<Line>) => set({ lines: s.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) })

  const body = (): DraftInput => ({
    customer_id: s.customer_id,
    email: s.email.trim() || null,
    items: s.lines.map((l) => (l.variant_id ? { variant_id: l.variant_id, quantity: l.quantity } : { title: l.title, unit_price: l.unit_price, quantity: l.quantity })),
    shipping_option_id: s.shipping_option_id || null,
    shipping_address: Object.keys(clean(s.address)).length > 1 ? clean(s.address) : null,
    note: s.note.trim() || null,
  })
  const doSave = () => {
    const a = s.address
    if (a.address_1?.trim()) {
      const problem = addressProblem(a as any, selling)
      if (problem) return toast.error(problem.field === "country_code" ? `${countryName(a.country_code)} isn't in any market. Add it in Settings → Markets first.` : problem.message)
    }
    if (s.shipping_option_id && rates.data && !rate) return toast.error("Choose a shipping rate for this country/region.")
    save.mutate(body(), {
      onSuccess: (r) => {
        toast.success(isNew ? "Draft order created" : "Draft order saved")
        if (isNew || r.draft.id !== id) navigate(`/orders/drafts/${r.draft.id}`, { replace: true })
      },
      onError: (e) => toast.error(errorMessage(e)),
    })
  }

  // A rate from another country's zone doesn't apply once the country changes.
  useEffect(() => {
    if (rates.data && s.shipping_option_id && !rates.data.rates.some((r) => r.id === s.shipping_option_id)) set({ shipping_option_id: "" })
  }, [rates.data, country]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorState error={error} />
  if (!isNew && !data) return <Text size="small">Loading…</Text>
  const draft = data?.draft
  const title = isNew ? "Create order" : draftLabel(draft!)

  return (
    <>
      <PageHeader
        back="/orders/drafts"
        title={title}
        subtitle={draft ? `Last updated ${formatDate(draft.updated_at)}` : undefined}
        actions={
          !isNew && writable ? (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild><Button size="small" variant="secondary">More actions</Button></DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setConfirmDelete(true)}>Delete draft order</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          ) : undefined
        }
      />
      <div className="grid gap-4 pb-24 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Section
            title="Products"
            actions={writable && (
              <div className="flex gap-2">
                <Button size="small" variant="secondary" onClick={() => setBrowse(true)}>Browse</Button>
                <Button size="small" variant="transparent" onClick={() => setCustom(true)}>Add custom item</Button>
              </div>
            )}
          >
            {s.lines.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-ui-fg-muted">Add products to this order</div>
            ) : (
              <ul className="divide-y divide-ui-border-base">
                {s.lines.map((l) => (
                  <li key={l.key} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <Thumb src={l.thumbnail} size={40} />
                    <div className="min-w-0 flex-1">
                      <Text size="small" weight="plus" className="truncate">{l.title}</Text>
                      <Text size="xsmall" className="text-ui-fg-subtle">{l.variant_id ? (l.subtitle && !/^default (variant|title)$/i.test(l.subtitle) ? l.subtitle + " · " : "") : "Custom item · "}{formatMoney(l.unit_price)}</Text>
                    </div>
                    <Input aria-label={`Quantity of ${l.title}`} type="number" min={1} max={10000} className="w-20" value={l.quantity} disabled={!writable} onChange={(e) => setLine(l.key, { quantity: Math.max(1, Math.min(10000, Number(e.target.value) || 1)) })} />
                    <Text size="small" className="w-20 text-right">{formatMoney(l.unit_price * l.quantity)}</Text>
                    {writable && <IconButton size="small" variant="transparent" aria-label={`Remove ${l.title}`} onClick={() => set({ lines: s.lines.filter((x) => x.key !== l.key) })}><XMark /></IconButton>}
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Payment">
            <div className="flex flex-col gap-2 p-4 text-[13px]">
              <Row label="Subtotal" sub={`${s.lines.reduce((a, l) => a + l.quantity, 0)} items`} value={formatMoney(subtotal)} />
              <Row label="Shipping" sub={rate?.name ?? "No shipping"} value={formatMoney(shipping)} />
              {!dirty && draft && draft.tax_total > 0 && <Row label="Taxes" value={formatMoney(draft.tax_total)} />}
              <Row label={<b>Total</b>} value={<b>{formatMoney(!dirty && draft ? draft.total : subtotal + shipping)}</b>} />
              {dirty && <Text size="xsmall" className="text-ui-fg-muted">Taxes are calculated when you save.</Text>}
              {!isNew && writable && (
                <div className="mt-2 flex flex-wrap justify-end gap-2">
                  <Button size="small" variant="secondary" disabled={dirty || !s.lines.length} onClick={() => setInvoicing(true)}>Send invoice</Button>
                  <Button size="small" variant="secondary" disabled={dirty || !s.lines.length} onClick={() => setConfirm("later")}>Payment due later</Button>
                  {can("payments:write") && <Button size="small" disabled={dirty || !s.lines.length} onClick={() => setConfirm("paid")}>Mark as paid</Button>}
                </div>
              )}
              {isNew && <Text size="xsmall" className="text-ui-fg-muted">Save the draft to collect payment.</Text>}
              {!isNew && draft?.invoice_sent_at && (
                <Text size="xsmall" className="text-right text-ui-fg-muted">Invoice sent to {draft.invoice_to} on {formatDate(draft.invoice_sent_at)}</Text>
              )}
            </div>
          </Section>
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Section title="Notes">
            <div className="p-4"><Textarea aria-label="Notes" rows={3} value={s.note} disabled={!writable} maxLength={5000} onChange={(e) => set({ note: e.target.value })} placeholder="No notes" /></div>
          </Section>
          <CustomerCard s={s} set={set} writable={writable} />
          <Section title="Shipping">
            <div className="flex flex-col gap-3 p-4">
              <AddressFields compact company={false} value={s.address as any} countries={selling} onChange={(address) => set({ address: address as DraftAddress })} disabled={!writable} />
              <Field label="Shipping rate">
                <Select value={s.shipping_option_id || "none"} onValueChange={(v) => set({ shipping_option_id: v === "none" ? "" : v })} disabled={!writable}>
                  <Select.Trigger><Select.Value /></Select.Trigger>
                  <Select.Content>
                    <Select.Item value="none">No shipping</Select.Item>
                    {rates.data?.rates.map((r) => <Select.Item key={r.id} value={r.id}>{r.name} · {formatMoney(r.price)}{r.free_over != null ? ` (free over ${formatMoney(r.free_over)})` : ""}</Select.Item>)}
                  </Select.Content>
                </Select>
              </Field>
            </div>
          </Section>
        </div>
      </div>
      {invoicing && draft && <InvoiceDialog draft={draft} onClose={() => setInvoicing(false)} />}
      <SaveBar visible={writable && (dirty || isNew)} message={isNew ? "Unsaved draft order" : "Unsaved changes"} onDiscard={() => (isNew ? navigate("/orders/drafts") : setS(base))} onSave={doSave} saving={save.isPending} disabled={!s.lines.length && !s.email && !s.customer_id} />
      {browse && <VariantPicker onClose={() => setBrowse(false)} onAdd={(adds) => set({ lines: mergeLines(s.lines, adds) })} />}
      {custom && <CustomItem onClose={() => setCustom(false)} onAdd={(l) => set({ lines: [...s.lines, { ...l, key: k() }] })} />}
      <Prompt open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>{confirm === "paid" ? "Mark as paid" : "Payment due later"}</Prompt.Title>
            <Prompt.Description>
              {confirm === "paid" ? `Creates the order and records a manual payment of ${formatMoney(draft?.total ?? 0)} (cash, bank transfer, etc).` : "Creates the order with payment pending. You can collect it later from the order."}
            </Prompt.Description>
          </Prompt.Header>
          <label className="flex items-center gap-2 px-6 pb-2 text-[13px]"><Checkbox checked={notify} onCheckedChange={(c) => setNotify(c === true)} /> Send order confirmation to the customer</label>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() =>
                complete.mutate(
                  { paid: confirm === "paid", notify },
                  { onSuccess: (r) => { toast.success("Order created"); navigate(`/orders/${r.order_id}`) }, onError: (e) => toast.error(errorMessage(e)) },
                )
              }
            >
              Create order
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {title}?</Prompt.Title>
            <Prompt.Description>This can't be undone.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => del.mutate(undefined, { onSuccess: () => { toast.success("Draft order deleted"); navigate("/orders/drafts") }, onError: (e) => toast.error(errorMessage(e)) })}>Delete</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  )
}

function Row({ label, sub, value }: { label: React.ReactNode; sub?: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-24 shrink-0">{label}</span>
      <span className="flex-1 truncate text-ui-fg-subtle">{sub}</span>
      <span>{value}</span>
    </div>
  )
}

function mergeLines(lines: Line[], adds: Omit<Line, "key">[]) {
  const out = [...lines]
  for (const a of adds) {
    const hit = out.find((l) => l.variant_id && l.variant_id === a.variant_id)
    if (hit) hit.quantity += a.quantity
    else out.push({ ...a, key: k() })
  }
  return out.map((l) => ({ ...l }))
}

function VariantPicker({ onClose, onAdd }: { onClose: () => void; onAdd: (l: Omit<Line, "key">[]) => void }) {
  const [q, setQ] = useState("")
  const { data, isLoading } = useVariantSearch(q)
  const [picked, setPicked] = useState<Map<string, Omit<Line, "key">>>(new Map())
  const toggle = (id: string, line: Omit<Line, "key">, on: boolean) =>
    setPicked((m) => {
      const n = new Map(m)
      on ? n.set(id, line) : n.delete(id)
      return n
    })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>Add products</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-3 overflow-y-auto">
          <SearchInput value={q} onChange={setQ} placeholder="Search products" />
          {isLoading && <Text size="small" className="text-ui-fg-muted">Loading…</Text>}
          <ul className="divide-y rounded-lg border">
            {data?.map((p) => (
              <li key={p.id} className="py-1">
                <div className="flex items-center gap-3 px-3 py-2">
                  <Thumb src={p.thumbnail} size={32} />
                  <Text size="small" weight="plus" className="flex-1 truncate">{p.title}</Text>
                  {p.status !== "published" && <Text size="xsmall" className="text-ui-fg-muted">{p.status}</Text>}
                </div>
                {p.variants.map((v: any) => {
                  const line = { variant_id: v.id, title: p.title, subtitle: v.title, thumbnail: p.thumbnail, unit_price: v.price, quantity: 1 }
                  return (
                    <label key={v.id} className="flex cursor-pointer items-center gap-3 py-1.5 pl-12 pr-3 hover:bg-ui-bg-base-hover">
                      <Checkbox checked={picked.has(v.id)} onCheckedChange={(c) => toggle(v.id, line, c === true)} aria-label={`${p.title} ${v.title}`} />
                      <Text size="small" className="flex-1 truncate">{p.variants.length > 1 ? v.title : "Default"}{v.sku ? <span className="text-ui-fg-muted"> · {v.sku}</span> : null}</Text>
                      <Text size="small">{formatMoney(v.price)}</Text>
                    </label>
                  )
                })}
              </li>
            ))}
            {data && !data.length && <li className="px-3 py-6 text-center text-[13px] text-ui-fg-muted">Nothing found</li>}
          </ul>
        </Drawer.Body>
        <Drawer.Footer>
          <Text size="small" className="mr-auto text-ui-fg-subtle">{picked.size} selected</Text>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!picked.size} onClick={() => (onAdd([...picked.values()]), onClose())}>Add</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

function CustomItem({ onClose, onAdd }: { onClose: () => void; onAdd: (l: Omit<Line, "key">) => void }) {
  const [title, setTitle] = useState("")
  const [price, setPrice] = useState("")
  const [qty, setQty] = useState("1")
  const valid = title.trim() && Number(price) >= 0 && price !== "" && Number(qty) >= 1
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>Add custom item</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-3">
          <Field label="Item name"><Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Price (USD)"><Input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
          <Field label="Quantity"><Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!valid} onClick={() => (onAdd({ title: title.trim(), unit_price: Math.round(Number(price) * 100) / 100, quantity: Math.floor(Number(qty)) }), onClose())}>Add item</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

function CustomerCard({ s, set, writable }: { s: State; set: (p: Partial<State>) => void; writable: boolean }) {
  const can = useCan()
  const [q, setQ] = useState("")
  const search = useQuery({
    queryKey: ["draft-customers", q],
    enabled: can("customers:read") && q.trim().length > 1,
    queryFn: () => sdk.client.fetch<{ customers: any[] }>("/admin/terify/customers", { query: { q: q.trim(), limit: 8 } }),
  })
  const pick = async (c: any) => {
    set({ customer_id: c.id, customer_label: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email, email: c.email })
    setQ("")
    // Prefill shipping address from the customer's default (Shopify does this).
    try {
      const { customer } = await sdk.admin.customer.retrieve(c.id, { fields: "*addresses" } as any)
      const a: any = (customer as any).addresses?.find((x: any) => x.is_default_shipping) ?? (customer as any).addresses?.[0]
      if (a) set({ customer_id: c.id, customer_label: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email, email: c.email, address: clean({ ...a, country_code: a.country_code ?? "us" }) })
    } catch {}
  }
  return (
    <Section title="Customer">
      <div className="flex flex-col gap-3 p-4">
        {s.customer_id ? (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {can("customers:read") ? <Link to={`/customers/${s.customer_id}`} className="text-[13px] font-medium text-ui-fg-interactive hover:underline">{s.customer_label}</Link> : <Text size="small" weight="plus">{s.customer_label}</Text>}
              <Text size="xsmall" className="truncate text-ui-fg-subtle">{s.email}</Text>
            </div>
            {writable && <IconButton size="small" variant="transparent" aria-label="Remove customer" onClick={() => set({ customer_id: null, customer_label: null, email: "" })}><XMark /></IconButton>}
          </div>
        ) : (
          <>
            {can("customers:read") && writable && (
              <div className="relative">
                <SearchInput value={q} onChange={setQ} placeholder="Search customers" />
                {search.data && q.trim().length > 1 && (
                  <ul className="absolute z-20 mt-1 w-full divide-y rounded-lg border bg-ui-bg-base shadow-elevation-flyout">
                    {search.data.customers.map((c) => (
                      <li key={c.id}><button type="button" className="w-full px-3 py-2 text-left text-[13px] hover:bg-ui-bg-base-hover" onClick={() => pick(c)}>{[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email}<span className="block text-[12px] text-ui-fg-subtle">{c.email}</span></button></li>
                    ))}
                    {!search.data.customers.length && <li className="px-3 py-2 text-[13px] text-ui-fg-muted">No customers found</li>}
                  </ul>
                )}
              </div>
            )}
            <Field label="Email" hint="Or enter an email for a guest order."><Input type="email" value={s.email} disabled={!writable} maxLength={200} onChange={(e) => set({ email: e.target.value })} /></Field>
          </>
        )}
      </div>
    </Section>
  )
}


/** Shopify's "Send invoice": email with a pay link; the customer pays at checkout and the draft becomes an order. */
function InvoiceDialog({ draft, onClose }: { draft: any; onClose: () => void }) {
  const send = useSendInvoice(draft.id)
  const [to, setTo] = useState(draft.email ?? "")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [link, setLink] = useState<string | null>(null)
  return (
    <Prompt open onOpenChange={(o) => !o && onClose()}>
      <Prompt.Content>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            send.mutate(
              { to: to.trim(), subject: subject.trim() || undefined, message: message.trim() || undefined },
              {
                onSuccess: (r) => {
                  toast.success(`Invoice sent to ${r.to}`)
                  if (r.invoice_url) setLink(r.invoice_url)
                  else onClose()
                },
                onError: (e) => toast.error(errorMessage(e)),
              }
            )
          }}
        >
          <Prompt.Header>
            <Prompt.Title>Send invoice</Prompt.Title>
            <Prompt.Description>The customer gets a secure link to pay at checkout. Once paid, the draft becomes an order.</Prompt.Description>
          </Prompt.Header>
          <div className="flex flex-col gap-3 px-6 pb-4">
            <label className="flex flex-col gap-1 text-[13px]">To<Input type="email" required value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <label className="flex flex-col gap-1 text-[13px]">Subject (optional)<Input value={subject} placeholder={`Invoice #D${draft.draft_no ?? ""}`} maxLength={200} onChange={(e) => setSubject(e.target.value)} /></label>
            <label className="flex flex-col gap-1 text-[13px]">Custom message (optional)<Textarea rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
            {link && (
              <div className="rounded-md bg-ui-bg-subtle p-2 text-[12px]">
                Email isn't switched on yet, so nothing was delivered. Test link: <a className="break-all text-ui-fg-interactive underline" href={link} target="_blank" rel="noreferrer">{link}</a>
              </div>
            )}
          </div>
          <Prompt.Footer>
            <Prompt.Cancel type="button" onClick={onClose}>{link ? "Close" : "Cancel"}</Prompt.Cancel>
            {!link && <Button type="submit" size="small" isLoading={send.isPending}>Send invoice</Button>}
          </Prompt.Footer>
        </form>
      </Prompt.Content>
    </Prompt>
  )
}
